import { useCallback, useEffect, useRef, useState } from 'react';
import {
  OverlayDialog,
} from '../components/overlay/OverlayDialog';
import type { OverlayKind, OverlayOptions } from '../types/overlay-dialog.type.ts';

interface OverlayState extends OverlayOptions {
  id: number;
  kind: OverlayKind;
  open: boolean;
  resolve: (value: boolean) => void;
}

type Trigger = (kind: OverlayKind, opts: OverlayOptions) => Promise<boolean>;

// 모듈 레벨 dispatcher — Provider 가 마운트되는 시점에 등록됨.
// 이 패턴 덕분에 컴포넌트 외부(이벤트 핸들러, async 함수 등)에서도 호출 가능.
let dispatcher: Trigger | null = null;

let uid = 0;

export function OverlayProvider({ children }: { children: React.ReactNode }) {
  const [stack, setStack] = useState<OverlayState[]>([]);
  // close → unmount 사이의 애니메이션 시간 확보용
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(
    new Map(),
  );

  const trigger = useCallback<Trigger>((kind, opts) => {
    return new Promise<boolean>((resolve) => {
      const id = ++uid;
      setStack((prev) => [
        ...prev,
        { id, kind, open: true, resolve, ...opts },
      ]);
    });
  }, []);

  useEffect(() => {
    dispatcher = trigger;
    return () => {
      dispatcher = null;
    };
  }, [trigger]);

  const handleResolve = (id: number, value: boolean) => {
    setStack((prev) => {
      const target = prev.find((o) => o.id === id);
      target?.resolve(value);
      // open=false 로 바꿔서 close animation 트리거
      return prev.map((o) => (o.id === id ? { ...o, open: false } : o));
    });

    // 애니메이션 끝난 뒤 stack 에서 제거
    const timer = setTimeout(() => {
      setStack((prev) => prev.filter((o) => o.id !== id));
      timersRef.current.delete(id);
    }, 200);
    timersRef.current.set(id, timer);
  };

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
    };
  }, []);

  return (
    <>
      {children}
      {stack.map(({ id, resolve: _r, ...rest }) => (
        <OverlayDialog
          key={id}
          {...rest}
          onResolve={(v) => handleResolve(id, v)}
        />
      ))}
    </>
  );
}

/**
 * 컴포넌트 안/밖 어디서든 호출 가능한 imperative alert.
 * Promise<true> 반환 (확인 누르거나 dismiss 시).
 */
export function showAlert(opts: OverlayOptions = {}): Promise<boolean> {
  if (!dispatcher) {
    console.warn(
      '[ui] alert() 호출 시점에 <OverlayProvider> 가 마운트되어 있지 않습니다.',
    );
    return Promise.resolve(false);
  }
  return dispatcher('alert', opts);
}

/**
 * Promise<boolean> 반환 — 확인=true, 취소/dismiss=false.
 */
export function showConfirm(opts: OverlayOptions = {}): Promise<boolean> {
  if (!dispatcher) {
    console.warn(
      '[ui] confirm() 호출 시점에 <OverlayProvider> 가 마운트되어 있지 않습니다.',
    );
    return Promise.resolve(false);
  }
  return dispatcher('confirm', opts);
}

/**
 * Hook 형태 선호 시. 그냥 위 함수들 wrap.
 * 핸들러 안에서 deps 로 안 들어가도 되어 약간 깔끔.
 */
export function useOverlay() {
  return { alert: showAlert, confirm: showConfirm };
}
